import PropTypes from 'prop-types';
import React from 'react';
import { Card, Button, EmptyState } from '@opendate-app/setlist';
import AdminChrome, { SearchIcon, openAdminSearch } from '../../AdminShared/AdminChrome';
import AdminPageHead from '../../AdminShared/AdminPageHead';
import '../styles/adminDashboard.css';

const JUMP_CHIPS = [
  ['Users', '/admin/users'], ['Teams', '/admin/teams'], ['Orders', '/admin/orders'],
  ['Calendar Events', '/admin/calendar_events'], ['Payouts', '/admin/payouts'],
  ['Risk Desk', '/admin/risk_desk'], ['Runtime Config', '/admin/runtime_config'],
];

const AdminDashboard = ({ chrome = {}, pulse = [], opsQueue = [], health = [], globalNumbers = [] }) => (
  <AdminChrome {...chrome}>
    <div className="adash-body">
      <AdminPageHead
        title="Dashboard"
        subtitle="Back office for the whole platform — find a record, watch the queues, clear the work."
      >
        <span className="adash-refreshed">Live &mdash; cached ~2m</span>
        <Button variant="secondary" onClick={() => window.location.reload()}>Refresh</Button>
        {chrome.sidekiqUrl ? <Button onClick={() => window.open(chrome.sidekiqUrl, '_blank')}>Sidekiq</Button> : null}
      </AdminPageHead>

      <Card>
        <div className="adash-card-pad">
          <button type="button" className="adash-searchbox" onClick={openAdminSearch} aria-label="Open search">
            <SearchIcon size={20} fill="#717680" />
            <span className="adash-searchbox-placeholder">Search users, events, orders, venues&hellip;</span>
            <span className="adash-search-kbd">&#8984;K</span>
          </button>
          <div className="adash-jump">
            <span className="adash-jump-label">Jump to</span>
            {JUMP_CHIPS.map(([label, path]) => <a className="adash-jump-chip" href={path} key={path}>{label}</a>)}
          </div>
        </div>
      </Card>

      {pulse.length ? (
        <div className="adash-pulse">
          {pulse.map((tile) => (
            <Card key={tile.label}>
              <div className="adash-tile">
                <span className="adash-tile-label">{tile.label}</span>
                <span className="adash-tile-value">{tile.value}</span>
                <span className={`adash-tile-delta adash-delta-${tile.delta.direction}`}>{tile.delta.text}</span>
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      <div className="adash-two-up">
        <Card className="adash-overflow">
          <div className="adash-card-head">
            <div>
              <h2>Needs a human</h2>
              <p>Queues where nothing moves until someone acts</p>
            </div>
          </div>
          {opsQueue.length ? opsQueue.map((row) => (
            <a className="adash-oprow" href={row.path} key={row.name}>
              <span className="adash-oprow-main">
                <span className="adash-oprow-name">{row.name}</span>
                <span className="adash-oprow-note">{row.note}</span>
              </span>
              <span className={`adash-count adash-count-${row.tone}`}>{row.count}</span>
            </a>
          )) : <div style={{ padding: 20 }}><EmptyState title="Nothing waiting" description="Every queue is clear." /></div>}
        </Card>

        <Card className="adash-overflow">
          <div className="adash-card-head">
            <div>
              <h2>Platform health</h2>
              <p>Background jobs</p>
            </div>
          </div>
          {health.length ? health.map((row) => (
            <div className="adash-healthrow" key={row.name}>
              <span className="adash-health-left">
                <span className={`adash-health-dot adash-health-dot-${row.status}`} />
                <span className="adash-oprow-main">
                  <span className="adash-oprow-name">{row.name}</span>
                  <span className="adash-oprow-note">{row.note}</span>
                </span>
              </span>
              <span className="adash-health-value">{row.value}</span>
            </div>
          )) : <div style={{ padding: 20 }}><EmptyState title="Health unavailable" description="Could not read Sidekiq stats." /></div>}
        </Card>
      </div>
    </div>

    <div className="adash-footer">
      <div className="adash-footer-inner">
        <span className="adash-footer-label">Global numbers</span>
        <div className="adash-footer-grid">
          {globalNumbers.map((row) => (
            <span className="adash-footer-num" key={row.label}>
              <strong>{row.count}</strong><span>{row.label}</span>
            </span>
          ))}
        </div>
        <div className="adash-copyright">CONFIDENTIAL &mdash; Do Not Disclose.</div>
      </div>
    </div>
  </AdminChrome>
);

AdminDashboard.propTypes = {
  chrome: PropTypes.object,
  pulse: PropTypes.array,
  opsQueue: PropTypes.array,
  health: PropTypes.array,
  globalNumbers: PropTypes.array,
};

export default AdminDashboard;
